import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import cogniLogo from "@/assets/cogni-logo.png";

const navigation = [
  { name: "Como Funciona", href: "/#como-funciona" },
  { name: "Clientes", href: "/#clientes" },
  { name: "Atuação", href: "/#atuacao" },
];

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (href: string) => {
    const sectionId = href.replace("/#", "");
    if (location.pathname === "/") {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);
    }
  };

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    scrollToSection(href);
  };

  const handleMobileNavClick = (href: string) => {
    setIsOpen(false);
    scrollToSection(href);
  };

  const handleDemoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById("agendar-demo");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById("agendar-demo");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border safe-top">
      <nav className="container-wide">
        <div className="flex h-14 sm:h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src={cogniLogo} alt="Cogni" className="h-7 sm:h-8 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-1">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="px-3 py-2 text-sm font-medium rounded-md transition-colors touch-target flex items-center text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer"
              >
                {item.name}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden xl:flex items-center gap-3">
            <a href="/#agendar-demo" onClick={handleDemoClick}>
              <Button size="sm" className="gradient-primary text-primary-foreground touch-target">
                Agendar demo
              </Button>
            </a>
          </div>

          {/* Mobile/Tablet Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="xl:hidden">
              <Button variant="ghost" size="icon" className="touch-target">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Abrir menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-[350px] p-0">
              <div className="flex flex-col h-full">
                <div className="p-5 border-b border-border">
                  <Link to="/" onClick={() => setIsOpen(false)}>
                    <img src={cogniLogo} alt="Cogni" className="h-7 w-auto" />
                  </Link>
                </div>
                <nav className="flex-1 overflow-y-auto p-4">
                  <div className="flex flex-col gap-1">
                    {navigation.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        onClick={(e) => {
                          e.preventDefault();
                          handleMobileNavClick(item.href);
                        }}
                        className="px-4 py-3.5 text-base font-medium rounded-lg transition-colors touch-target flex items-center text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer"
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                </nav>
                <div className="p-5 border-t border-border space-y-3 safe-bottom">
                  <a
                    href="/#agendar-demo"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(false);
                      handleDemoClick(e);
                    }}
                    className="block"
                  >
                    <Button className="w-full gradient-primary text-primary-foreground touch-target">
                      Agendar demo
                    </Button>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
